import { LoanApplicationPayload } from '../types/loan';
import { api } from '../api/client';

export interface FormDraft {
  id: string;
  userId: string;
  formData: LoanApplicationPayload;
  lastSaved: Date;
  step: number;
  isComplete: boolean;
}

class FormDraftService {
  private readonly DRAFT_KEY = 'loan_application_draft';
  private readonly DRAFT_EXPIRY_DAYS = 7; // Drafts expire after 7 days

  /**
   * Save form draft to localStorage
   */
  saveDraftLocally(formData: LoanApplicationPayload, step: number = 0): void {
    try {
      const draft: FormDraft = {
        id: this.generateDraftId(),
        userId: this.getCurrentUserId(),
        formData: { ...formData },
        lastSaved: new Date(),
        step,
        isComplete: false
      };

      localStorage.setItem(this.DRAFT_KEY, JSON.stringify(draft));

    } catch (error) {
      console.error('Failed to save form draft locally:', error);
    }
  }

  /**
   * Load form draft from localStorage
   */
  loadDraftLocally(): FormDraft | null {
    try {
      const draftData = localStorage.getItem(this.DRAFT_KEY);
      if (!draftData) return null;

      const draft: FormDraft = JSON.parse(draftData);
      
      // Check if draft is expired
      if (this.isDraftExpired(draft)) {
        this.clearDraftLocally();
        return null;
      }

      return draft;
    } catch (error) {
      console.error('Failed to load form draft locally:', error);
      return null;
    }
  }

  /**
   * Clear form draft from localStorage
   */
  clearDraftLocally(): void {
    localStorage.removeItem(this.DRAFT_KEY);

  }

  /**
   * Save form draft to server
   */
  async saveDraftToServer(formData: LoanApplicationPayload, step: number = 0): Promise<boolean> {
    try {
      const draft: FormDraft = {
        id: this.generateDraftId(),
        userId: this.getCurrentUserId(),
        formData: { ...formData },
        lastSaved: new Date(),
        step,
        isComplete: false
      };

      const payload = {
        draftId: draft.id,
        userId: draft.userId,
        formType: 'LOAN_APPLICATION',
        formData: JSON.stringify(draft.formData),
        currentStep: draft.step,
        isComplete: draft.isComplete
      };

      const response = await api.post('/api/v1/form-drafts', payload);

      if (response && response.status >= 200 && response.status < 300) {
        const savedDraft = response.data;

        return true;
      }

      console.error('Failed to save draft to server:', response?.status, response?.statusText);
      this.saveDraftLocally(formData, step);
      return false;
    } catch (error: any) {
      console.error('Failed to save form draft to server:', error?.message || error);
      // Fallback to local storage
      this.saveDraftLocally(formData, step);
      return false;
    }
  }

  /**
   * Load form draft from server
   */
  async loadDraftFromServer(): Promise<FormDraft | null> {
    try {
      const userId = this.getCurrentUserId();
      const response = await api.get(`/api/v1/form-drafts/active`, {
        params: { userId, formType: 'LOAN_APPLICATION' }
      });

      if (response && response.status === 200) {
        const serverDraft = response.data;
        const draft: FormDraft = {
          id: serverDraft.draftId,
          userId: serverDraft.userId,
          formData: JSON.parse(serverDraft.formData),
          lastSaved: new Date(serverDraft.lastSaved),
          step: serverDraft.currentStep,
          isComplete: serverDraft.isComplete
        };

        return draft;
      }

      if (response && response.status === 404) {
        return this.loadDraftLocally();
      }

      console.error('Failed to load draft from server:', response?.status, response?.statusText);
      return this.loadDraftLocally();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return this.loadDraftLocally();
      }
      console.error('Failed to load form draft from server:', error?.message || error);
      return this.loadDraftLocally();
    }
  }

  /**
   * Auto-save form data with debouncing
   */
  private autoSaveTimeout: NodeJS.Timeout | null = null;

  autoSave(formData: LoanApplicationPayload, step: number = 0, delay: number = 2000): void {
    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    // Set new timeout
    this.autoSaveTimeout = setTimeout(async () => {
      // Try to save to server first, fallback to local storage
      const serverSaved = await this.saveDraftToServer(formData, step);
      if (!serverSaved) {
        this.saveDraftLocally(formData, step);
      }
    }, delay);
  }

  /**
   * Check if there's a saved draft
   */
  hasDraft(): boolean {
    return this.loadDraftLocally() !== null;
  }

  /**
   * Get draft info without loading full data
   */
  getDraftInfo(): { hasDraft: boolean; lastSaved?: Date; step?: number } {
    const draft = this.loadDraftLocally();
    return {
      hasDraft: draft !== null,
      lastSaved: draft?.lastSaved,
      step: draft?.step
    };
  }

  /**
   * Generate unique draft ID
   */
  private generateDraftId(): string {
    return `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID from localStorage
   */
  private getCurrentUserId(): string {
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        return user.id || user.userId || 'anonymous';
      } catch (error) {
        console.error('Failed to parse user info:', error);
      }
    }
    return 'anonymous';
  }

  /**
   * Check if draft is expired
   */
  private isDraftExpired(draft: FormDraft): boolean {
    const now = new Date();
    const draftDate = new Date(draft.lastSaved);
    const daysDiff = (now.getTime() - draftDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > this.DRAFT_EXPIRY_DAYS;
  }

  /**
   * Restore form data from draft
   */
  restoreFormData(): { formData: LoanApplicationPayload; step: number } | null {
    const draft = this.loadDraftLocally();
    if (!draft) return null;

    return {
      formData: draft.formData,
      step: draft.step
    };
  }

  /**
   * Mark draft as complete (when form is successfully submitted)
   */
  markDraftComplete(): void {
    const draft = this.loadDraftLocally();
    if (draft) {
      draft.isComplete = true;
      localStorage.setItem(this.DRAFT_KEY, JSON.stringify(draft));
    }
  }
}

export const formDraftService = new FormDraftService();
